import { VerifiedItem } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified";
import { IncomeField, ItemDocument } from "./types";
import {
  documentTotal,
  earliestDate,
  getItemDocument,
  inclusiveDaysBetween,
  isFraudulentItem,
  latestDate,
  parseDate
} from "./utils";
import { IncomeSourceProvider } from "shared/types";
import { toMoney } from "shared/utils";

/**
 * Inscribe paystubs are pooled into a single period by the API.
 * CalculatePaystubMonthlyTotals sums every document, then spans min start -> max end.
 */
const inscribePaystubFormula = (
  items: VerifiedItem[],
  field: IncomeField
): string | null => {
  const documents = items
    .filter((item) => !isFraudulentItem(item))
    .map(getItemDocument)
    .filter((document): document is ItemDocument => Boolean(document));
  if (!documents.length) return null;

  const totalAmount = toMoney(
    documents.reduce((sum, document) => sum + documentTotal(document, field), 0)
  );

  const earliest = earliestDate(
    documents.map((document) => parseDate(document.start_date))
  );
  const latest = latestDate(
    documents.map((document) => parseDate(document.end_date))
  );
  const daysBetween = inclusiveDaysBetween(earliest, latest);

  return `(${totalAmount}/${daysBetween} days)*365/12`;
};

/**
 * Ocrolus paystubs are annualized per document and then added together, so each
 * document contributes its own period.
 */
const ocrolusPaystubFormula = (
  items: VerifiedItem[],
  field: IncomeField
): string | null => {
  const documentFormulas = items.flatMap((item) => {
    const document = getItemDocument(item);
    const sources = document?.income_sources ?? [];
    if (!document || !sources.length) return [];

    const total = documentTotal(document, field);
    const earliest = earliestDate(
      sources.map((source) => parseDate(source.start_date))
    );
    const latest = latestDate(
      sources.map((source) => parseDate(source.end_date))
    );
    const daysBetween = inclusiveDaysBetween(earliest, latest);
    if (!total || !daysBetween) return [];

    return [`(${toMoney(total)}/${daysBetween} days)`];
  });
  if (!documentFormulas.length) return null;

  return documentFormulas.length > 1
    ? `(${documentFormulas.join("+")})*365/12`
    : `${documentFormulas[0]}*365/12`;
};

export const PaystubTooltip = ({
  items,
  field,
  provider,
  displayedMonthly
}: {
  items: VerifiedItem[];
  field: IncomeField;
  provider: string | null;
  displayedMonthly: number;
}) => {
  const formula =
    provider === IncomeSourceProvider.Inscribe
      ? inscribePaystubFormula(items, field)
      : ocrolusPaystubFormula(items, field);
  if (!formula) return null;

  return (
    <>
      Verified {field} monthly calculation
      <br />
      {formula} = {toMoney(displayedMonthly)}
    </>
  );
};
